/**
 * Canonical Effect implementation of the doc-pack READ surface: the
 * catalog-open ritual (64KiB prefix probe → header → extend to blobBaseOffset
 * → catalog) plus per-page blob fetches with range coalescing. Errors are born
 * in the typed channel ({@link DocPackRangeFetchError} /
 * {@link DocPackCorruptError} / {@link DocPackPageAbsentError}) so consumers
 * use `catchTag(s)`; anything outside the taxonomy is a codec bug and dies.
 *
 * The default transport is the `HttpClient` service (provide
 * `FetchHttpClient.layer` or a platform client). This is the ONLY entry for
 * ranged reads — frontend consumers run these effects through the shared atoms
 * in `@listening/frontend/doc-pack/source`; there is no Promise facade.
 */
import { HttpClient } from 'effect/http';
import { Cause, Context, Duration, Effect, Predicate, Schema } from 'effect';
import {
  decodeDocPackPage,
  docPackPageByteRange,
  readDocPackCatalog,
  readDocPackHeader,
  resolveDocPackRangesWithWords as resolveDocPackRangesWithWordsSync,
  type DocPackResolvedRanges,
} from './read';
import {
  buildDocPackSource as buildDocPackSourceSync,
  buildDocPackSourceWithWordLocations as buildDocPackSourceWithWordLocationsSync,
  encodeDocPackSource as encodeDocPackSourceCore,
  parseDocPackSource as parseDocPackSourceSync,
  type BuiltDocPack,
} from './build';
import {
  DocPackBuildInputError,
  DocPackCorruptError,
  DocPackEncodeError,
  DocPackError,
  DocPackPageAbsentError,
  DocPackRangeFetchError,
  type DocPackBuildInput,
  type DocPackCatalog,
  type DocPackPage,
  type DocPackPageCharRange,
  type DocPackSource,
} from './types';

/**
 * Reads bytes `[start, endInclusive]` of the pack. May return fewer bytes than
 * requested when the object ends before `endInclusive` (S3 clamps 206 bodies to
 * the object size); callers that know the exact expected length treat a short
 * result as a corrupt pack.
 */
export type DocPackRangeFetcher = (
  start: number,
  endInclusive: number,
) => Promise<Uint8Array>;

/**
 * First probe size: large enough that most packs' header + catalog fit in one
 * request; when the catalog extends past it the opener issues one follow-up
 * fetch up to `blobBaseOffset`.
 */
export const DOC_PACK_CATALOG_PREFIX_BYTES = 65536;

/**
 * Bounds a hung doc-pack connection so readers degrade (frontend falls back to
 * the legacy chapter transport, backends fail the request) instead of spinning
 * forever. Generous enough for a ~4MB inline blob on a slow link; a normal
 * catalog/page range fetch resolves in well under a second.
 */
export const DOC_PACK_FETCH_TIMEOUT_MS = 30_000;

/**
 * Merge page byte ranges whose gap is at most this many bytes into one request:
 * catalog pages are laid out contiguously in the blob region, so requested
 * pages usually coalesce into a single range, and over-reading a small gap is
 * cheaper than paying another round trip.
 */
export const DOC_PACK_RANGE_COALESCE_GAP_BYTES = 65536;

/** Non-coalescible range groups are fetched in parallel, capped at this. */
const DOC_PACK_RANGE_FETCH_CONCURRENCY = 4;

export type DocPackReadOptions = {
  /** Injectable range-read seam; defaults to `HttpClient` with a `Range` header. */
  readonly fetchRange?: DocPackRangeFetcher | undefined;
  /** Timeout used by the DEFAULT fetcher; ignored when `fetchRange` is injected. */
  readonly timeoutMs?: number | undefined;
  /** First catalog probe size. Only tests should shrink this. */
  readonly catalogPrefixBytes?: number | undefined;
};

/** A catalog opened from a URL; page fetches slice the blob region after it. */
export type LoadedDocPack = {
  readonly url: string;
  readonly catalog: DocPackCatalog;
  readonly blobBaseOffset: number;
};

const errorMessage = (error: unknown) =>
  String(error instanceof Error ? error.message : error);

const isDocPackReadError = (
  error: unknown,
): error is DocPackRangeFetchError | DocPackCorruptError =>
  Predicate.isTagged(error, 'DocPackRangeFetchError') ||
  Predicate.isTagged(error, 'DocPackCorruptError');

type RangeFetchEffect = (
  start: number,
  endInclusive: number,
) => Effect.Effect<Uint8Array, DocPackRangeFetchError | DocPackCorruptError>;

/**
 * Default range reader over the `HttpClient` service. A 206 body is the
 * requested range (possibly clamped at end-of-object). A 200 means the server
 * IGNORED the `Range` header and returned the FULL object, so the requested
 * window is sliced out of it — never trust a 200 body to be range bytes: a
 * full object that happens to be no longer than the requested window is NOT
 * the window.
 */
const httpRangeFetch =
  (
    http: HttpClient.HttpClient,
    url: string,
    timeoutMs: number,
  ): RangeFetchEffect =>
  (start, endInclusive) =>
    Effect.gen(function* () {
      const res = yield* http
        .get(url, {
          headers: {
            Range: `bytes=${start.toString()}-${endInclusive.toString()}`,
          },
        })
        .pipe(
          Effect.mapError((error) =>
            DocPackRangeFetchError.make({
              message: `doc-pack range fetch failed: ${errorMessage(error.cause ?? error)}`,
              details: {
                url,
                range: [start, endInclusive],
                cause: error.cause ?? error,
              },
            }),
          ),
        );
      if (res.status !== 206 && res.status !== 200) {
        return yield* DocPackRangeFetchError.make({
          message: `doc-pack range fetch failed: ${res.status.toString()}`,
          details: { url, range: [start, endInclusive], status: res.status },
        });
      }
      const body = new Uint8Array(
        yield* res.arrayBuffer.pipe(
          Effect.mapError((error) =>
            DocPackRangeFetchError.make({
              message: `doc-pack range fetch failed: ${errorMessage(error.cause ?? error)}`,
              details: {
                url,
                range: [start, endInclusive],
                cause: error.cause ?? error,
              },
            }),
          ),
        ),
      );
      // `subarray` clamps, so a full body that ends before the window yields a
      // short (possibly empty) result — surfaced as corrupt by length-checked
      // callers rather than silently decoded as the wrong bytes.
      return res.status === 200 ? body.subarray(start, endInclusive + 1) : body;
    }).pipe(
      Effect.timeoutOrElse({
        duration: Duration.millis(timeoutMs),
        orElse: () =>
          Effect.fail(
            DocPackRangeFetchError.make({
              message: `doc-pack range fetch timed out after ${timeoutMs.toString()}ms`,
              details: { url, range: [start, endInclusive] },
            }),
          ),
      }),
    );

/**
 * Run an injected Promise fetcher, keeping its own typed errors and wrapping
 * unknown rejections as transient fetch failures (the seam is external code —
 * a thrown string or TypeError is a transport problem, not a codec bug).
 */
const injectedRangeFetch =
  (fetchRange: DocPackRangeFetcher, url: string): RangeFetchEffect =>
  (start, endInclusive) =>
    Effect.tryPromise({
      try: () => fetchRange(start, endInclusive),
      catch: (error) =>
        isDocPackReadError(error)
          ? error
          : DocPackRangeFetchError.make({
              message: `doc-pack range fetch failed: ${errorMessage(error)}`,
              details: { url, range: [start, endInclusive], cause: error },
            }),
    });

export const DocPackLocalRanges = Context.Reference<
  (url: string) => DocPackRangeFetcher | undefined
>('@listening/doc-pack/DocPackLocalRanges', {
  defaultValue: () => () => undefined,
});

const resolveRangeFetch = (
  url: string,
  options: DocPackReadOptions | undefined,
): Effect.Effect<RangeFetchEffect, never, HttpClient.HttpClient> =>
  Effect.gen(function* () {
    const fetchRange = options?.fetchRange ?? (yield* DocPackLocalRanges)(url);
    if (fetchRange) return injectedRangeFetch(fetchRange, url);
    const http = yield* HttpClient.HttpClient;
    return httpRangeFetch(
      http,
      url,
      options?.timeoutMs ?? DOC_PACK_FETCH_TIMEOUT_MS,
    );
  });

/**
 * Fetch a range whose exact length is known from the catalog. A short result
 * means the stored object is shorter than its catalog claims — corrupt, not
 * range bytes.
 */
const fetchExactRange = (
  fetchRange: RangeFetchEffect,
  url: string,
  start: number,
  endInclusive: number,
) =>
  Effect.gen(function* () {
    const bytes = yield* fetchRange(start, endInclusive);
    const expected = endInclusive - start + 1;
    if (bytes.length !== expected) {
      return yield* DocPackCorruptError.make({
        message: `doc-pack range ${start.toString()}-${endInclusive.toString()} returned ${bytes.length.toString()} bytes, expected ${expected.toString()}: pack does not match its catalog`,
        details: { url },
      });
    }
    return bytes;
  });

/** Decode failures mean the stored blob is broken — corrupt, whatever threw. */
const decodePage = (url: string, pageNumber: number, blob: Uint8Array) =>
  Effect.tryPromise({
    try: () => decodeDocPackPage(pageNumber, blob),
    catch: (error) =>
      DocPackCorruptError.make({
        message: `doc-pack page ${pageNumber.toString()} decode failed: ${errorMessage(error)}`,
        details: { url, page: pageNumber, cause: error },
      }),
  });

/**
 * Decode one page's already-fetched compressed blob. For callers that slice
 * pages out of a region they downloaded themselves (frontend full-blob path).
 */
export const decodeDocPackPageBlob = decodePage;

/**
 * Read one raw byte range of the pack at `url` (no catalog interpretation).
 * This is the transport under every other read; exposed for callers that
 * slice regions themselves, e.g. the frontend full-blob path.
 */
export const fetchDocPackByteRange = Effect.fn('DocPack.fetchByteRange')(
  function* (
    url: string,
    start: number,
    endInclusive: number,
    options?: DocPackReadOptions,
  ) {
    const fetchRange = yield* resolveRangeFetch(url, options);
    return yield* fetchRange(start, endInclusive);
  },
);

/**
 * Open a doc-pack's catalog from a URL: probe the first
 * {@link DOC_PACK_CATALOG_PREFIX_BYTES}, read the header, and — when the
 * catalog extends past the probe — fetch the remainder up to `blobBaseOffset`
 * in one follow-up request.
 */
export const openDocPackCatalogFromUrl = Effect.fn(
  'DocPack.openCatalogFromUrl',
)(function* (url: string, options?: DocPackReadOptions) {
  const fetchRange = yield* resolveRangeFetch(url, options);
  const prefixBytes =
    options?.catalogPrefixBytes ?? DOC_PACK_CATALOG_PREFIX_BYTES;
  let prefix = yield* fetchRange(0, prefixBytes - 1);
  // A first probe shorter than requested means the range clamped at
  // end-of-object, so `prefix.length` is the EXACT object size. A header whose
  // catalog region then claims to extend past that size is corrupt: without
  // this check the opener would `fetchRange` bytes that don't exist and pad the
  // shortfall with zeros (see the follow-up merge below), then inflate garbage.
  const objectSizeIfComplete =
    prefix.length < prefixBytes ? prefix.length : undefined;
  const header = yield* Effect.try({
    try: () => readDocPackHeader(prefix),
    catch: (error) =>
      DocPackCorruptError.make({
        message: `doc-pack catalog read failed: ${errorMessage(error)}`,
        details: { url, cause: error },
      }),
  });
  if (
    objectSizeIfComplete !== undefined &&
    header.blobBaseOffset > objectSizeIfComplete
  ) {
    return yield* DocPackCorruptError.make({
      message: `doc-pack catalog claims ${header.blobBaseOffset.toString()} bytes but the object is only ${objectSizeIfComplete.toString()} — corrupt header`,
      details: { url },
    });
  }
  if (prefix.length < header.blobBaseOffset) {
    const rest = yield* fetchRange(prefix.length, header.blobBaseOffset - 1);
    const merged = new Uint8Array(header.blobBaseOffset);
    merged.set(prefix);
    merged.set(rest, prefix.length);
    prefix = merged;
  }
  const { catalog, blobBaseOffset } = yield* Effect.tryPromise({
    try: () => readDocPackCatalog(prefix),
    catch: (error) =>
      DocPackCorruptError.make({
        message: `doc-pack catalog read failed: ${errorMessage(error)}`,
        details: { url, cause: error },
      }),
  });
  return { url, catalog, blobBaseOffset } satisfies LoadedDocPack;
});

/**
 * Fetch one page's compressed blob bytes. Fails with
 * {@link DocPackPageAbsentError} when the page is not in the catalog.
 */
export const fetchDocPackPageBytes = Effect.fn('DocPack.fetchPageBytes')(
  function* (
    pack: LoadedDocPack,
    pageNumber: number,
    options?: DocPackReadOptions,
  ) {
    const range = docPackPageByteRange(
      pack.catalog,
      pack.blobBaseOffset,
      pageNumber,
    );
    if (!range) {
      return yield* DocPackPageAbsentError.make({ pageNumber });
    }
    const fetchRange = yield* resolveRangeFetch(pack.url, options);
    return yield* fetchExactRange(
      fetchRange,
      pack.url,
      range.start,
      range.endInclusive,
    );
  },
);

/**
 * Fetch and decode one page. Fails with {@link DocPackPageAbsentError} when
 * the page is not in the catalog.
 */
export const fetchDocPackPage = Effect.fn('DocPack.fetchPage')(function* (
  pack: LoadedDocPack,
  pageNumber: number,
  options?: DocPackReadOptions,
) {
  const blob = yield* fetchDocPackPageBytes(pack, pageNumber, options);
  return yield* decodePage(pack.url, pageNumber, blob);
});

/**
 * Fetch and decode many pages. Pages absent from the catalog are silently
 * skipped (callers that must reject on absence use {@link fetchDocPackPage}),
 * so absence is not in this error channel. Adjacent/near-adjacent byte ranges
 * are coalesced into merged range requests (see
 * {@link DOC_PACK_RANGE_COALESCE_GAP_BYTES}); non-coalescible groups are
 * fetched in parallel with bounded concurrency.
 */
export const fetchDocPackPages = Effect.fn('DocPack.fetchPages')(function* (
  pack: LoadedDocPack,
  pageNumbers: Iterable<number>,
  options?: DocPackReadOptions,
) {
  const fetchRange = yield* resolveRangeFetch(pack.url, options);
  const wanted = [...new Set(pageNumbers)]
    .flatMap((pageNumber) => {
      const range = docPackPageByteRange(
        pack.catalog,
        pack.blobBaseOffset,
        pageNumber,
      );
      return range ? [{ pageNumber, range }] : [];
    })
    .toSorted((a, b) => a.range.start - b.range.start);

  type Group = {
    start: number;
    endInclusive: number;
    pages: {
      pageNumber: number;
      range: { start: number; endInclusive: number };
    }[];
  };
  const groups: Group[] = [];
  for (const page of wanted) {
    const current = groups.at(-1);
    if (
      current &&
      page.range.start - current.endInclusive - 1 <=
        DOC_PACK_RANGE_COALESCE_GAP_BYTES
    ) {
      current.endInclusive = Math.max(
        current.endInclusive,
        page.range.endInclusive,
      );
      current.pages.push(page);
    } else {
      groups.push({
        start: page.range.start,
        endInclusive: page.range.endInclusive,
        pages: [page],
      });
    }
  }

  const pages = new Map<number, DocPackPage>();
  yield* Effect.forEach(
    groups,
    (group) =>
      Effect.gen(function* () {
        const bytes = yield* fetchExactRange(
          fetchRange,
          pack.url,
          group.start,
          group.endInclusive,
        );
        for (const { pageNumber, range } of group.pages) {
          const blob = bytes.subarray(
            range.start - group.start,
            range.endInclusive + 1 - group.start,
          );
          pages.set(pageNumber, yield* decodePage(pack.url, pageNumber, blob));
        }
      }),
    { concurrency: DOC_PACK_RANGE_FETCH_CONCURRENCY, discard: true },
  );
  return pages;
});

/**
 * Typed-channel wrapper over the sync codec resolver. `DocPackError` here
 * means the caller's ranges don't fit the supplied pages (missing page / bad
 * offsets) — for API handlers that's the client's reference, typically a 400.
 * The sync codec stays throw-based on purpose (it is a hot path shared with
 * non-Effect readers); this boundary lifts its tagged throw into the channel.
 */
export const resolveDocPackRangesWithWords = (
  pages: ReadonlyMap<number, DocPackPage>,
  ranges: readonly DocPackPageCharRange[],
): Effect.Effect<DocPackResolvedRanges, DocPackError> =>
  Effect.try({
    try: () => resolveDocPackRangesWithWordsSync(pages, ranges),
    catch: (error) =>
      Predicate.isTagged(error, 'DocPackError')
        ? (error as DocPackError)
        : new Cause.UnknownError(error, 'doc-pack range resolve failed'),
  }).pipe(Effect.catchTag('UnknownError', (error) => Effect.die(error)));

// =============================================================================
// BUILDER SURFACE
//
// Typed-channel wrappers over the sync/Promise builder cores in `build.ts`
// (the READ equivalents above wrap `read.ts`). The cores stay throw-based —
// they are shared with non-Effect callers and tests — and throw the tagged
// {@link DocPackBuildInputError}; these boundaries lift that throw into the
// error channel so a build/parse failure is a FAILURE the caller can `catchTag`
// or a surrounding `catchAll` catches, NOT a defect that escapes it. This is the
// ONLY builder entry for Effect callers (materialize-chapters, the BuildDocPack
// activity); there is no Promise facade.
// =============================================================================

/** Pass a thrown tagged build-input error through; wrap anything else as one. */
const asBuildInputError = (error: unknown): DocPackBuildInputError =>
  Schema.is(DocPackBuildInputError)(error)
    ? error
    : DocPackBuildInputError.make({
        message: `doc-pack build input error: ${errorMessage(error)}`,
      });

/** Validate + normalize an untrusted `DocPackSource` projection. */
export const parseDocPackSource = (
  value: unknown,
): Effect.Effect<DocPackSource, DocPackBuildInputError> =>
  Effect.try({
    try: () => parseDocPackSourceSync(value),
    catch: asBuildInputError,
  });

/** Build a `DocPackSource` from chapter/OCR input (may reject corrupt geometry). */
export const buildDocPackSource = (
  input: DocPackBuildInput,
): Effect.Effect<DocPackSource, DocPackBuildInputError> =>
  Effect.try({
    try: () => buildDocPackSourceSync(input),
    catch: asBuildInputError,
  });

export const buildDocPackSourceWithWordLocations = (input: DocPackBuildInput) =>
  Effect.try({
    try: () => buildDocPackSourceWithWordLocationsSync(input),
    catch: asBuildInputError,
  });

/**
 * Encode a built source into pack bytes. Two typed failure modes, mirroring the
 * read surface's environmental-vs-corrupt split: a geometry/projection problem
 * in the source is {@link DocPackBuildInputError} (permanent — the data will
 * never encode), while a deflate/platform rejection is {@link DocPackEncodeError}
 * (environmental — a caller may retry or skip the pack). Both are in the channel
 * so a surrounding `catchAll`/`catchTag` catches them instead of a defect.
 */
export const encodeDocPackSource = (
  source: DocPackSource,
  contentVersion?: number,
): Effect.Effect<BuiltDocPack, DocPackBuildInputError | DocPackEncodeError> =>
  Effect.tryPromise({
    try: () => encodeDocPackSourceCore(source, contentVersion),
    catch: (error) =>
      Schema.is(DocPackBuildInputError)(error)
        ? error
        : DocPackEncodeError.make({
            message: `doc-pack encode failed: ${errorMessage(error)}`,
          }),
  });

/** Build + encode in one step (parallel to the Promise `buildDocPack` core). */
export const buildDocPack = (
  input: DocPackBuildInput,
): Effect.Effect<BuiltDocPack, DocPackBuildInputError | DocPackEncodeError> =>
  buildDocPackSource(input).pipe(
    Effect.flatMap((source) => encodeDocPackSource(source)),
  );
